<template>
  <v-container fluid class="pa-6">
    <!-- Header -->
    <v-sheet
      class="pa-4 pa-md-6 mb-6 rounded-xl"
      color="primary"
      dark
      elevation="4"
    >
      <!-- Mobile Layout -->
      <div class="d-block d-md-none">
        <div class="d-flex align-center mb-3">
          <v-avatar color="white" size="40" class="mr-3">
            <v-icon color="primary" size="24">mdi-view-dashboard</v-icon>
          </v-avatar>
          <div class="flex-grow-1">
            <h1 class="text-h5 font-weight-bold mb-0">Dashboard</h1>
            <p class="text-caption mb-0 white--text text--lighten-2">
              Control de Asistencias
            </p>
          </div>
          <v-chip color="white" text-color="primary" x-small>
            <v-icon left x-small>mdi-clock-outline</v-icon>
            En vivo
          </v-chip>
        </div>
        <div class="d-flex">
          <v-btn
            color="white"
            text
            small
            class="rounded-lg font-weight-bold mr-2 flex-grow-1"
            elevation="0"
            @click="registrationDialog = true"
          >
            <v-icon left small>mdi-account-plus</v-icon>
            <span class="d-none d-sm-inline">Registrar</span>
            <span class="d-inline d-sm-none">+</span>
          </v-btn>
          <v-btn
            color="white"
            text
            small
            class="rounded-lg font-weight-bold flex-grow-1"
            elevation="0"
            @click="inicializar"
            :loading="loading"
          >
            <v-icon left small>mdi-refresh</v-icon>
            <span class="d-none d-sm-inline">Actualizar</span>
            <span class="d-inline d-sm-none">↻</span>
          </v-btn>
        </div>
      </div>

      <!-- Desktop Layout -->
      <div class="d-none d-md-flex align-center justify-space-between">
        <div>
          <div class="d-flex align-center mb-2">
            <v-avatar color="white" size="48" class="mr-4">
              <v-icon color="primary" size="28">mdi-view-dashboard</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold mb-0">Dashboard</h1>
              <p class="text-subtitle-1 mb-0 white--text text--lighten-2">
                Sistema de Control de Asistencias
              </p>
            </div>
          </div>
        </div>
        <div class="d-flex align-center">
          <v-chip class="mr-4" color="white" text-color="primary" small>
            <v-icon left small>mdi-clock-outline</v-icon>
            En tiempo real
          </v-chip>
          <v-btn
            color="white"
            text
            class="rounded-lg font-weight-bold mr-3"
            elevation="0"
            @click="registrationDialog = true"
          >
            <v-icon left>mdi-account-plus</v-icon>
            Registrar Acceso
          </v-btn>
          <v-btn
            color="white"
            text
            class="rounded-lg font-weight-bold"
            elevation="0"
            @click="inicializar"
            :loading="loading"
          >
            <v-icon left>mdi-refresh</v-icon>
            Actualizar
          </v-btn>
        </div>
      </div>
    </v-sheet>

    <!-- Stats -->
    <stats-cards
      :total="stats.total"
      :activos="stats.activos"
      :con-salida="stats.conSalida"
      :hoy="stats.hoy"
      class="mb-4"
    />

    <!-- Table -->
    <visitors-table :items="datos" :loading="loading" @refresh="inicializar" />

    <!-- Registration Dialog -->
    <registration-dialog
      v-model="registrationDialog"
      @success="onRegistrationSuccess"
      @error="onRegistrationError"
    />

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      top
      right
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar.show = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import StatsCards from "@/components/admin/StatsCards.vue";
import VisitorsTable from "@/components/admin/VisitorsTable.vue";
import RegistrationDialog from "@/components/admin/RegistrationDialog.vue";

export default {
  name: "Admin",
  components: { StatsCards, VisitorsTable, RegistrationDialog },
  data() {
    return {
      loading: false,
      datos: [],
      // búsqueda general
      searchText: "",
      // estadísticas
      stats: { total: 0, activos: 0, conSalida: 0, hoy: 0 },
      // dialog
      registrationDialog: false,
      // notifications
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  created() {
    this.inicializar();
  },
  methods: {
    async inicializar() {
      this.loading = true;
      try {
        const { data } = await axios.get(
          process.env.VUE_APP_BACKEND_ENVIRONMENT + "asistencias"
        );
        this.datos = Array.isArray(data) ? data : [];
        console.log(this.datos);
        this.computeStats();
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    computeStats() {
      const total = this.datos.length;
      const activos = this.datos.filter((r) => !r.salida).length;
      const conSalida = this.datos.filter((r) => !!r.salida).length;
      const hoy = this.datos.filter(
        (r) => this.dateOnly(r.entrada) === this.today()
      ).length;
      this.stats = { total, activos, conSalida, hoy };
    },
    today() {
      return new Date().toISOString().substr(0, 10);
    },
    dateOnly(iso) {
      if (!iso) return "";
      try {
        return new Date(iso).toISOString().substr(0, 10);
      } catch {
        return "";
      }
    },
    formatDateTime(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      const date = d.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      const time = d.toLocaleTimeString("es-AR", {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${date} ${time}`;
    },
    onRegistrationSuccess(event) {
      this.snackbar = {
        show: true,
        message: event.message,
        color: "success",
      };
      // Recargar datos para mostrar el nuevo registro
      this.inicializar();
    },
    onRegistrationError(event) {
      this.snackbar = {
        show: true,
        message: event.message,
        color: "error",
      };
    },
  },
  watch: {
    datos: {
      deep: true,
      handler() {
        this.computeStats();
      },
    },
  },
};
</script>

<style scoped></style>
